// The horizon: the day drawn as a band from when you got up to lights out,
// with now as a lit point on it, and a sky tint behind the app that follows
// the real clock (cool at dawn, clear by day, amber into the evening, deep
// indigo at night). It is there to make time visible, the thing an ADHD day
// loses first; it never counts anything. Pure, so it is tested without a DOM.

/** How the app looks, chosen per device. `auto` follows the clock between day and night. */
export type Appearance = 'auto' | 'light' | 'dark' | 'black' | 'glass' | 'workspace'
export const APPEARANCES: Appearance[] = ['auto', 'light', 'dark', 'black', 'glass', 'workspace']

/** What an appearance comes to at a given moment: a surface set, or the workspace's own theme. */
export type Resolved = 'day' | 'night' | 'black' | 'glass' | 'workspace'

/** Daylight for `auto`: 07:00 until 19:00 local. Fixed, so the switch is predictable. */
export const DAY_STARTS = 7 * 60
export const DAY_ENDS = 19 * 60

export const minuteOfDay = (now: number) => {
  const d = new Date(now)
  return d.getHours() * 60 + d.getMinutes()
}

export const isDaylight = (now: number) => {
  const m = minuteOfDay(now)
  return m >= DAY_STARTS && m < DAY_ENDS
}

export const resolveAppearance = (a: Appearance, now: number): Resolved =>
  a === 'auto' ? (isDaylight(now) ? 'day' : 'night') : a === 'light' ? 'day' : a === 'dark' ? 'night' : a

// The sky through a day: minute of day → colour. Between two stops the colour
// is mixed, so the tint moves by the minute rather than jumping at an hour.
const SKY: [number, string][] = [
  [0, '#1c1b4d'],
  [330, '#2a2968'],
  [400, '#8a5a8c'],
  [480, '#4b7ab8'],
  [720, '#4a86cc'],
  [1010, '#5a82b8'],
  [1110, '#d08a45'],
  [1190, '#7a4690'],
  [1290, '#2f2b72'],
  [1440, '#1c1b4d'],
]

const hex = (h: string) => [1, 3, 5].map((i) => parseInt(h.slice(i, i + 2), 16)) as [number, number, number]
const toHex = (c: number[]) => `#${c.map((v) => Math.round(v).toString(16).padStart(2, '0')).join('')}`

/** The sky's colour at a minute of the day. */
export const skyAt = (minute: number): string => {
  const m = ((minute % 1440) + 1440) % 1440
  for (let i = 1; i < SKY.length; i++) {
    const [m1, c1] = SKY[i]!
    const [m0, c0] = SKY[i - 1]!
    if (m <= m1) {
      const t = (m - m0) / (m1 - m0)
      const a = hex(c0)
      const b = hex(c1)
      return toHex(a.map((v, k) => v + (b[k]! - v) * t))
    }
  }
  return SKY[0]![1]
}

export interface Band {
  /** minutes of the day the band runs between */
  start: number
  end: number
  /** where now sits along it, 0..1 */
  now: number
  /** where the evening begins (the recap time), 0..1, if it falls on the band */
  evening?: number
}

const hhmm = (s: string | null | undefined): number | undefined => {
  const m = s ? /^(\d{1,2}):(\d{2})/.exec(s) : null
  return m ? Number(m[1]) * 60 + Number(m[2]) : undefined
}

/**
 * The band for today: from when you woke (the Day record's woke_at, else 07:00)
 * to lights out (23:30, or later if you are still up), with the evening marked
 * at the recap time. Before you are up, now sits at the start.
 */
export const band = (now: number, wokeAt?: string, recap?: string | null): Band => {
  const m = minuteOfDay(now)
  const woke = wokeAt ? minuteOfDay(new Date(wokeAt).getTime()) : undefined
  const start = woke !== undefined && woke <= m ? woke : Math.min(DAY_STARTS, m)
  const end = Math.max(23 * 60 + 30, m + 15)
  const at = (x: number) => Math.min(1, Math.max(0, (x - start) / (end - start)))
  const ev = hhmm(recap)
  return { start, end, now: at(m), evening: ev !== undefined && ev > start && ev < end ? at(ev) : undefined }
}

/** "07:40" from a minute of the day. */
export const clockOf = (minute: number) =>
  `${String(Math.floor(minute / 60) % 24).padStart(2, '0')}:${String(minute % 60).padStart(2, '0')}`
